import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

// 简单的内存缓存，避免重复请求同一个 tweet
const tweetCache = new Map();

const TweetCard = ({ postId }) => {
  const [tweet, setTweet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // 检查缓存
    if (tweetCache.has(postId)) {
      setTweet(tweetCache.get(postId));
      setLoading(false);
      return;
    }

    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    fetch(`${backendUrl}/api/tweets/${postId}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
      })
      .then(data => {
        tweetCache.set(postId, data); // 缓存结果
        setTweet(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching tweet:', err);
        setError(true);
        setLoading(false);
      });
  }, [postId]);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Box sx={{ mb: 2, p: 3, background: 'rgba(255,255,255,0.03)', borderRadius: 3, textAlign: 'center' }}>
        <CircularProgress size={20} sx={{ color: '#6b7280' }} />
      </Box>
    );
  }

  if (error || !tweet) {
    return (
      <Box sx={{ mb: 2, p: 2, background: 'rgba(255,255,255,0.03)', borderRadius: 3 }}>
        <Typography sx={{ color: '#6b7280', fontSize: '13px' }}>Failed to load post</Typography>
      </Box>
    );
  }

  return (
    <Box
      onClick={() => window.open(`https://x.com/${tweet.author_handle}/status/${postId}`, '_blank')}
      sx={{
        mb: 2,
        p: 2,
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        overflow: 'hidden',
        maxWidth: '100%',
        '&:hover': {
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(99, 102, 241, 0.3)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      {/* Author Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <img
          src={`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/image-proxy?url=${encodeURIComponent(tweet.author_avatar)}`}
          alt={tweet.author_name}
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.1)'
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', lineHeight: 1.2 }}>
            {tweet.author_name}
          </Typography>
          <Typography sx={{ color: '#6b7280', fontSize: '13px' }}>
            @{tweet.author_handle} · {formatDate(tweet.created_at)}
          </Typography>
        </Box>
        {/* X Logo */}
        <Box sx={{ color: '#6b7280' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </Box>
      </Box>

      {/* Tweet Text */}
      <Typography sx={{
        color: '#e5e7eb',
        fontSize: '14px',
        lineHeight: 1.5,
        mb: 2,
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        wordBreak: 'break-word',
        whiteSpace: 'normal'
      }}>
        {tweet.text}
      </Typography>

      {/* Metrics Row */}
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ color: '#6b7280', fontSize: '13px' }}>💬</Typography>
          <Typography sx={{ color: '#6b7280', fontSize: '13px' }}>{formatNumber(tweet.reply_count)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ color: '#6b7280', fontSize: '13px' }}>🔁</Typography>
          <Typography sx={{ color: '#6b7280', fontSize: '13px' }}>{formatNumber(tweet.repost_count)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ color: '#6b7280', fontSize: '13px' }}>❤️</Typography>
          <Typography sx={{ color: '#6b7280', fontSize: '13px' }}>{formatNumber(tweet.like_count)}</Typography>
        </Box>
        {tweet.view_count && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ color: '#6b7280', fontSize: '13px' }}>👁</Typography>
            <Typography sx={{ color: '#6b7280', fontSize: '13px' }}>{formatNumber(tweet.view_count)}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TweetCard;
